import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TabsContent } from '@/components/ui/tabs';
import {
  validacoesComiteGestor,
  type ValidacaoCreditoFornecedor,
} from '@/lib/data/validacao-comite-gestor';
import { formatCurrency } from '@/lib/utils/formatters';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle } from 'lucide-react';
import { useContext, useState } from 'react';
import { ApuracaoDetalhadaContext } from '@/contexts/ApuracaoDetalhadaContext';

export function ResumoGeralTab() {
  const ctx = useContext(ApuracaoDetalhadaContext);
  if (!ctx) {
    throw new Error('ResumoGeralTab deve ser usado dentro de <ApuracaoDetalhadaProvider>');
  }

  const [fornecedores] = useState<ValidacaoCreditoFornecedor[]>(validacoesComiteGestor);

  const temValidacaoComiteGestor = fornecedores.length > 0;

  const { apuracaoSaidas, buscaAplicada, isLoadingTabela, resumoMensal } = ctx;

  // Crédito declarado é o que o servidor devolve nas linhas de crédito (SPEC-002).
  const creditosDeclarados = resumoMensal.credito;

  // Saídas (Débitos): números reais das notas, do servidor (SPEC-006, §3, item 4).
  const totalSaidas = {
    valorOperacao: apuracaoSaidas?.ValorOperacao ?? 0,
    baseCalculoIBS: apuracaoSaidas?.BaseCalculoIBS ?? 0,
    valorIBS: apuracaoSaidas?.ValorIBS ?? 0,
    baseCalculoCBS: apuracaoSaidas?.BaseCalculoCBS ?? 0,
    valorCBS: apuracaoSaidas?.ValorCBS ?? 0,
  };

  // Baixa de estoque por perda: reduz o crédito do período (SPEC-006, decisão 7).
  const estorno = {
    ibs: apuracaoSaidas?.EstornoIBS ?? 0,
    cbs: apuracaoSaidas?.EstornoCBS ?? 0,
  };
  const temEstorno = estorno.ibs > 0 || estorno.cbs > 0;

  const temDados = buscaAplicada && !!apuracaoSaidas;
  const valor = (n: number) =>
    isLoadingTabela ? (
      <Skeleton className="h-6 w-28 inline-block" />
    ) : temDados ? (
      formatCurrency(n)
    ) : (
      '—'
    );

  const creditosAprovados = {
    ibs: fornecedores
      .filter(f => f.status === 'APROVADO')
      .reduce((acc, f) => acc + f.creditoIBS, 0),
    cbs: fornecedores
      .filter(f => f.status === 'APROVADO')
      .reduce((acc, f) => acc + f.creditoCBS, 0),
  };

  const creditosGlosados = {
    ibs: fornecedores.filter(f => f.status === 'GLOSADO').reduce((acc, f) => acc + f.creditoIBS, 0),
    cbs: fornecedores.filter(f => f.status === 'GLOSADO').reduce((acc, f) => acc + f.creditoCBS, 0),
  };

  const saldoAposValidacao = {
    ibs: totalSaidas.valorIBS - creditosAprovados.ibs,
    cbs: totalSaidas.valorCBS - creditosAprovados.cbs,
  };

  return (
    <TabsContent value="resumo-geral" className="space-y-6">
      <div className="bg-primary text-primary-foreground px-6 py-3 rounded-lg">
        <h2 className="text-xl font-bold">Resumo Geral da Apuração</h2>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="border-2">
          <CardHeader className="bg-muted/30">
            <CardTitle className="text-lg">Entradas (Créditos)</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-start gap-3 pb-3 border-b">
              <AlertCircle className="h-5 w-5 text-muted-foreground mt-0.5" />
              <p className="text-sm text-muted-foreground">
                Aguardando o cálculo do crédito (SPEC-002).
              </p>
            </div>

            {temEstorno && (
              <div className="space-y-2 text-amber-800">
                <p className="text-sm font-semibold">
                  Estorno de crédito (baixa de estoque por perda)
                </p>
                <div className="flex justify-between items-center">
                  <span className="text-sm">IBS:</span>
                  <span className="font-semibold">- {valor(estorno.ibs)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">CBS:</span>
                  <span className="font-semibold">- {valor(estorno.cbs)}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Reduz o crédito do período quando ele for calculado.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-2 border-red-200 bg-red-50/30">
          <CardHeader className="bg-red-100/50">
            <CardTitle className="text-lg text-red-900">Saídas (Débitos)</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div className="flex justify-between items-center pb-3 border-b">
              <span className="text-sm text-muted-foreground">Valor Total das Operações:</span>
              <span className="font-semibold">{valor(totalSaidas.valorOperacao)}</span>
            </div>

            <div className="flex justify-between items-center pb-3 border-b">
              <span className="text-sm text-muted-foreground">Base de Cálculo IBS:</span>
              <span className="font-semibold">{valor(totalSaidas.baseCalculoIBS)}</span>
            </div>

            <div className="flex justify-between items-center pb-3">
              <span className="text-base font-semibold">Débito IBS:</span>
              <span className="text-xl font-bold text-red-600">{valor(totalSaidas.valorIBS)}</span>
            </div>

            <div className="flex justify-between items-center pb-3 border-b">
              <span className="text-sm text-muted-foreground">Base de Cálculo CBS:</span>
              <span className="font-semibold">{valor(totalSaidas.baseCalculoCBS)}</span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-base font-semibold">Débito CBS:</span>
              <span className="text-xl font-bold text-red-600">{valor(totalSaidas.valorCBS)}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-2 border-muted">
        <CardHeader className="bg-muted/30">
          <CardTitle className="text-lg">Saldo a Pagar (Declarado)</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div className="space-y-1">
              <p className="font-semibold">Aguardando o cálculo do crédito (SPEC-002)</p>
              <p className="text-sm text-muted-foreground">
                O saldo é o débito menos o crédito do período. O débito já está acima
                {temEstorno
                  ? ', e o estorno de crédito reduzirá o crédito quando ele for calculado'
                  : ''}
                .
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-2 border-lime-300">
        <CardContent className="pt-6 space-y-6">
          <div className="bg-yellow-100 border border-yellow-300 text-yellow-900 px-6 py-3 rounded-lg flex items-center gap-3">
            <AlertCircle className="h-5 w-5" />
            <div>
              <h2 className="text-lg font-bold">Validação Comitê Gestor</h2>
              <p className="text-sm">
                Créditos validados com base na confirmação de pagamento aos fornecedores
              </p>
            </div>
          </div>

          {!temValidacaoComiteGestor ? (
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div className="space-y-1">
                    <p className="font-semibold">Validação ainda não disponível</p>
                    <p className="text-sm text-muted-foreground">
                      Esta seção compara os créditos da sua escrituração com a situação de cada um
                      na Apuração Assistida, documento a documento. A integração ainda não está no
                      ar — assim que estiver, os valores aparecem aqui automaticamente.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid gap-6 md:grid-cols-3">
                <Card className="border-2 border-blue-200">
                  <CardHeader className="bg-blue-50/50 pb-2 pt-4">
                    <CardTitle className="text-base">Créditos Declarados</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">IBS:</span>
                      <span className="text-lg font-semibold">
                        {formatCurrency(creditosDeclarados.ibs)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">CBS:</span>
                      <span className="text-lg font-semibold">
                        {formatCurrency(creditosDeclarados.cbs)}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 border-muted">
                  <CardHeader className="bg-muted/30 pb-2 pt-4">
                    <CardTitle className="text-base">Créditos Aprovados</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">IBS:</span>
                      <span className="text-lg font-semibold">
                        {formatCurrency(creditosAprovados.ibs)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">CBS:</span>
                      <span className="text-lg font-semibold">
                        {formatCurrency(creditosAprovados.cbs)}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 border-red-200 bg-red-50/30">
                  <CardHeader className="bg-red-100/50 pb-2 pt-4">
                    <CardTitle className="text-base text-red-900">Créditos Glosados</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">IBS:</span>
                      <span className="text-lg font-semibold text-red-600">
                        {formatCurrency(creditosGlosados.ibs)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">CBS:</span>
                      <span className="text-lg font-semibold text-red-600">
                        {formatCurrency(creditosGlosados.cbs)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardContent className="pt-6">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>CFOP</TableHead>
                        <TableHead>FORNECEDOR</TableHead>
                        <TableHead>CNPJ</TableHead>
                        <TableHead className="text-right">VALOR OPERAÇÃO</TableHead>
                        <TableHead className="text-right">CRÉDITO IBS</TableHead>
                        <TableHead className="text-right">CRÉDITO CBS</TableHead>
                        <TableHead>DATA PGTO</TableHead>
                        <TableHead>STATUS</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {fornecedores.map((fornecedor, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono">{fornecedor.cfop}</TableCell>
                          <TableCell>{fornecedor.fornecedor}</TableCell>
                          <TableCell className="font-mono text-sm">{fornecedor.cnpj}</TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(fornecedor.valorOperacao)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(fornecedor.creditoIBS)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(fornecedor.creditoCBS)}
                          </TableCell>
                          <TableCell>{fornecedor.dataPgto || '-'}</TableCell>
                          <TableCell>
                            {fornecedor.status === 'GLOSADO' ? (
                              <div className="space-y-1">
                                <Badge className="bg-red-500 hover:bg-red-600">Glosado</Badge>
                                <p className="text-xs text-muted-foreground">
                                  {fornecedor.dataPgto
                                    ? 'Pagamento não confirmado'
                                    : 'Aguardando comprovação de pagamento'}
                                </p>
                              </div>
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <Card className="border-2 border-lime-300 bg-lime-50/30">
                <CardHeader className="bg-lime-100/50 mx-3 py-3 rounded-lg border border-lime-200">
                  <CardTitle className="text-lg">
                    Saldo a Pagar (Após Validação Comitê Gestor)
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">IBS</p>
                      <p className="text-4xl font-bold text-lime-600">
                        {formatCurrency(saldoAposValidacao.ibs)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Débito: {formatCurrency(totalSaidas.valorIBS)} - Crédito Aprovado:{' '}
                        {formatCurrency(creditosAprovados.ibs)}
                      </p>
                      <p className="text-sm font-semibold text-red-600">
                        Crédito Glosado: {formatCurrency(creditosGlosados.ibs)}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">CBS</p>
                      <p className="text-4xl font-bold text-lime-600">
                        {formatCurrency(saldoAposValidacao.cbs)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Débito: {formatCurrency(totalSaidas.valorCBS)} - Crédito Aprovado:{' '}
                        {formatCurrency(creditosAprovados.cbs)}
                      </p>
                      <p className="text-sm font-semibold text-red-600">
                        Crédito Glosado: {formatCurrency(creditosGlosados.cbs)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </CardContent>
      </Card>
    </TabsContent>
  );
}
